"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const features = [{
  number: "01",
  title: "Engenharia de Precisão.",
  body: "Sistemas de pulverização desenvolvidos com tecnologia de ponta, garantindo a máxima eficiência e controlo rigoroso em cada gota aplicada no processo industrial.",
  specLabel: "Precisão",
  specValue: "+99.9%"
}, {
  number: "02",
  title: "Bocais de Alta Performance.",
  body: "Componentes de engenharia avançada que minimizam o desperdício e maximizam o rendimento, superando as limitações dos sistemas de pulverização convencionais.",
  specLabel: "Eficiência",
  specValue: "0.03% erro"
}, {
  number: "03",
  title: "Sistemas Automatizados.",
  body: "Soluções integradas que isolam variáveis críticas, garantindo uma operação contínua e estável, mesmo em ambientes industriais de alta exigência e complexidade.",
  specLabel: "Controlo",
  specValue: "360 graus"
}, {
  number: "04",
  title: "Otimização de Processos.",
  body: "Simulação e análise de fluxo que posicionam cada bocal no ponto exato, mantendo a consistência da aplicação mesmo sob condições operacionais variáveis.",
  specLabel: "Latência",
  specValue: "18 ms"
}, {
  number: "05",
  title: "Durabilidade Industrial.",
  body: "Equipamentos construídos para suportar ciclos de trabalho intensivos, com manutenção simplificada e longa vida útil em ambientes de produção contínua.",
  specLabel: "Manutenção",
  specValue: "5 min/ciclo"
}];
export default function Engineering() {
  const container = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const root = container.current;
    const leftEl = leftRef.current;
    const rightEl = rightRef.current;
    if (!root || !leftEl || !rightEl) return;
    const mm = gsap.matchMedia();
    mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
      ScrollTrigger.create({
        trigger: leftEl,
        start: "top 96px",
        endTrigger: rightEl,
        end: "bottom bottom-=120",
        pin: leftEl,
        pinSpacing: false,
        anticipatePin: 1
      });
      gsap.set(".void-card", {
        y: 60
      });
      gsap.utils.toArray<HTMLElement>(".void-card").forEach((card, i) => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          delay: i * 0.12,
          scrollTrigger: {
            trigger: card,
            start: "top 78%",
            once: true
          }
        });
      });
      gsap.set(".void-left-child", {
        y: 20
      });
      gsap.to(".void-left-child", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.09,
        scrollTrigger: {
          trigger: leftEl,
          start: "top 82%",
          once: true
        }
      });
    });
    mm.add("(max-width: 1023px) and (prefers-reduced-motion: no-preference)", () => {
      gsap.set(".void-card", {
        y: 40
      });
      gsap.utils.toArray<HTMLElement>(".void-card").forEach((card, i) => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          delay: i * 0.1,
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            once: true
          }
        });
      });
      gsap.set(".void-left-child", {
        y: 20
      });
      gsap.to(".void-left-child", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.09,
        scrollTrigger: {
          trigger: leftEl,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".void-card", ".void-left-child"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31108" id="precisionsprayingsolutions" data-nav="dark" className="bg-background text-foreground relative overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-10 py-32 md:py-56">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className="lg:col-span-4 lg:self-start">
            <div className="flex flex-col gap-8">
              <span className="void-left-child text-xs font-medium uppercase tracking-[0.22em] text-primary" style={{
              opacity: 0
            }}>
                Engenharia
              </span>

              <h2 className="void-left-child font-default text-foreground text-[clamp(2.5rem,5.4vw,5rem)] font-normal leading-[0.98] tracking-[-0.03em]" style={{
              opacity: 0
            }}>Processos Químicos&nbsp;</h2>

              <p className="void-left-child text-muted-foreground text-[clamp(1.0625rem,1.15vw,1.25rem)] font-light leading-[1.55] max-w-md" style={{
              opacity: 0
            }}>
                A Eurocontrol projeta sistemas de pulverização com rigor técnico. Cada componente é otimizado para garantir eficiência máxima e resultados consistentes na sua indústria.
              </p>

              <div className="void-left-child pt-2" style={{
              opacity: 0
            }}>
                <Link to="/ProductsPage" className="group inline-flex items-center gap-2 text-foreground text-sm underline underline-offset-[6px] decoration-border hover:decoration-primary transition-colors">
                  <span>Ver catálogo técnico</span>
                  <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:-rotate-45" strokeWidth={1.25} />
                </Link>
              </div>

              <div className="void-left-child pt-6" style={{
              opacity: 0
            }}>
                <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-secondary h-auto px-6 py-3.5 text-sm font-medium">
                  <Link to="/ContactsPage" className="inline-flex items-center gap-3">
                    <span>Consulte-nos</span>
                    <span className="inline-flex size-7 items-center justify-center rounded-lg bg-background text-foreground">
                      <ArrowUpRight className="size-3.5" strokeWidth={1.5} />
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div ref={rightRef} className="lg:col-span-8 flex flex-col gap-6">
            {features.map((feature, i) => {
            const isInverted = i === 2;
            return <article data-index={i} key={feature.number} className={"void-card p-8 md:p-10 rounded-[28px] " + (isInverted ? "bg-muted text-foreground" : "bg-card border border-border shadow-sm")} style={{
              opacity: 0
            }}>
                  <div className="flex flex-col gap-6">
                    <div className={"text-xs font-medium tracking-[0.02em] " + (isInverted ? "text-primary" : "text-secondary")}>
                      {feature.number}
                    </div>

                    <h3 className={"font-default text-[clamp(1.5rem,2.2vw,2.125rem)] font-medium leading-[1.08] tracking-[-0.02em] text-foreground"}>
                      {feature.title}
                    </h3>

                    <p className={"text-base leading-[1.6] max-w-2xl text-muted-foreground"}>
                      {feature.body}
                    </p>

                    <div className={"flex items-center gap-3 pt-4 mt-2 border-t border-border"}>
                      <span className={"text-[0.8125rem] tracking-[0.01em] text-muted-foreground"}>
                        {feature.specLabel}
                      </span>
                      <span className={"text-[0.8125rem] tracking-[0.01em] text-muted-foreground"}>
                        {"·"}
                      </span>
                      <span className={"text-[0.9375rem] font-medium tracking-[-0.005em] text-foreground"}>
                        {feature.specValue}
                      </span>
                    </div>
                  </div>
                </article>;
          })}
          </div>
        </div>
      </div>
    </section>;
}